import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation, Routes, Route, useParams } from 'react-router-dom';
import {
    getAllCardsForUser,
    Card,
    deleteCard,
    getBoardList,
    getListsByBoardId,
    getCardById,
    updateCard,
    createCard, getProjectList
} from '../api';
import Button from '../components/common/Button';
import CardDetailModal from '../components/board/CardDetailModal';
import CreateCardSelectModal from '../components/board/CreateCardSelectModal';
import ResponsiveHeader from "../components/common/ResponsiveHeader";
import useAuth from "../hooks/useAuth"; // ✨ [추가] 모달 임포트

// --- 유틸 함수 (ms 포맷팅) ---
const formatMsShort = (ms?: number) => {
    if (ms === undefined || ms === null) return '-';
    const totalSeconds = Math.floor(ms / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;

    const parts = [];
    if (h > 0) parts.push(`${h}시간`);
    if (m > 0) parts.push(`${m}분`);
    if (s > 0 || parts.length === 0) parts.push(`${s}초`);
    return parts.join(' ');
};

// ✨ [신규] 상태값 색상/라벨 매핑 (모달과 동일)
const getStateColor = (state?: string) => {
    switch (state) {
        case 'IDLE': return '#FFD54F';
        case 'FOLLOWUP': return '#BA68C8';
        case 'COMPLETED': return '#81C784';
        case 'PROCESS': return '#64B5F6';
        default: return 'transparent';
    }
};

const getStateLabel = (state?: string) => {
    switch (state) {
        case 'IDLE': return '대기';
        case 'FOLLOWUP': return '팔로업';
        case 'COMPLETED': return '완료';
        case 'PROCESS': return '진행중';
        default: return '-';
    }
};

// ✨ [신규] 리스트용 상태 배지 스타일
const ListStatePill = styled.span<{ bgColor: string }>`
    background-color: ${props => props.bgColor};
    color: #000;
    padding: 0.1rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: bold;
    display: inline-block;
    min-width: 50px;
    text-align: center;
`;

// --- 스타일 정의 ---
const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: var(--bg-body);
`;

const FilterCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-normal);
  font-size: 0.9rem;
  cursor: pointer;
  user-select: none;

  input { accent-color: var(--primary-accent); cursor: pointer; }
  &:hover { color: var(--text-bright); }
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: var(--bg-input);
  padding: 0.25rem;
  border-radius: 4px;
  border: 1px solid var(--border-color);

  /* ✨ 모바일: 검색창 100% 너비 */
  @media (max-width: 768px) {
    width: 100%;
    flex-grow: 1;
  }
`;

const SearchSelect = styled.select`
  background-color: transparent;
  color: var(--text-bright);
  border: none;
  outline: none;
  font-size: 0.9rem;
  cursor: pointer;
  option {
    background-color: var(--bg-card);
    color: var(--text-normal);
  }
`;

const SearchInput = styled.input`
  background-color: transparent;
  color: var(--text-bright);
  border: none;
  outline: none;
  font-size: 0.9rem;
  width: 200px;

  &::placeholder {
    color: var(--text-dark);
  }

  &::-webkit-calendar-picker-indicator {
    filter: invert(1);
    cursor: pointer;
  }

  /* ✨ 모바일: 입력 필드 유동적 너비 */
  @media (max-width: 768px) {
    width: 100%;
    min-width: 0; /* flex item 축소 허용 */
    flex: 1;
  }
`;

const ResetSortButton = styled.button`
  background: none;
  border: 1px solid var(--border-color);
  color: var(--text-normal);
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  &:hover { background-color: var(--bg-input); color: var(--text-bright); }
`;

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 1rem;
  position: relative;
  /* ✨ z-index를 설정하지 않거나 헤더(100)보다 낮게 유지하되, 
     내부 모달은 반드시 fixed 포지션이어야 합니다. */
`;

const ListSection = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: auto;
  border-radius: 8px;
  background-color: var(--bg-card);
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);

  &::-webkit-scrollbar { width: 8px; height: 8px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background-color: var(--text-dark); border-radius: 4px; border: 2px solid var(--bg-body);
  }
`;

const TaskTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;

  thead {
    position: sticky;
    top: 0;
    z-index: 10;
  }

  th {
    background-color: var(--bg-list);
    font-weight: bold;
    color: var(--text-normal);
    padding: 0.75rem 0.5rem;
    text-align: left;
    border-bottom: 2px solid var(--border-color);
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 10;
    box-shadow: 0 2px 0 var(--border-color);
    &:hover { background-color: var(--bg-input); color: var(--text-bright); }
  }

  td {
    padding: 0.75rem 0.5rem;
    text-align: left;
    border-bottom: 1px solid var(--border-color);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    background-color: inherit;
  }

  /* ✨ [수정] 모바일 반응형 스타일: 컬럼 인덱스 재조정 */
  @media (max-width: 768px) {
    /* 덜 중요한 컬럼 숨기기 (진행시간, 라벨, 리스트, 보드, 생성일, 수정일) */
    .mobile-hidden {
      display: none;
    }

    /* 1. 체크박스 컬럼 */
    th:nth-child(1), td:nth-child(1) {
      width: 30px !important;
      padding-left: 0.25rem;
      padding-right: 0.25rem;
    }

    /* 2. 상태 컬럼: 좁게 고정 (기존에 제목이던 위치 -> 상태로 변경됨) */
    th:nth-child(2), td:nth-child(2) {
      width: 60px !important;
      text-align: center;
      padding: 0.5rem 0.25rem;
    }

    /* 3. 제목 컬럼: 남는 공간 차지 (auto) 및 줄바꿈 허용 (기존에 날짜던 위치 -> 제목으로 변경됨) */
    th:nth-child(3), td:nth-child(3) {
      width: auto !important;
      white-space: normal;
    }

    /* 4. 날짜 컬럼: 너비 고정 (새로 4번째로 밀림) */
    th:nth-child(4), td:nth-child(4) {
      width: 85px !important;
      font-size: 0.85rem;
    }

    td {
      padding: 0.5rem 0.25rem;
      font-size: 0.85rem;
    }
  }
`;

// status props를 DOM(tr)으로 전달하지 않도록 구조 분해 할당으로 분리
const TaskRow = styled(({ status, ...rest }: { status?: string } & React.ComponentProps<'tr'>) => (
    <tr {...rest} />
))`
  /* status 값에 따라 배경색 변경 */
  background-color: ${({ status }) =>
          status === 'IN_PROGRESS' ? '#FFF9C4' : 'var(--bg-card)'
  };

  /* 진행 중일 때 텍스트 색상을 검정으로 (가독성) */
  color: ${({ status }) =>
          status === 'IN_PROGRESS' ? '#000000' : 'inherit'
  };

  /* 내부 td에도 스타일 강제 적용 */
  td {
    background-color: ${({ status }) =>
            status === 'IN_PROGRESS' ? '#FFF9C4' : 'var(--bg-card)'
    };
    color: ${({ status }) =>
            status === 'IN_PROGRESS' ? '#000000' : 'inherit'
    };
  }

  /* 호버 효과 */
  &:hover td {
    background-color: ${({ status }) =>
            status === 'IN_PROGRESS' ? '#FFF59D' : 'rgba(97, 175, 239, 0.1)'
    } !important;
    cursor: pointer;
  }
`;

const CheckboxDisplay = styled.div<{ checked: boolean }>`
  width: 16px; height: 16px;
  border: 2px solid ${props => props.checked ? '#98c379' : 'var(--text-dark)'};
  background-color: ${props => props.checked ? '#98c379' : 'transparent'};
  border-radius: 4px;
  display: inline-block;
  cursor: pointer; /* 클릭 가능하도록 변경 */
  transition: all 0.2s;

  &:hover {
    border-color: ${props => props.checked ? '#98c379' : 'var(--primary-accent)'};
  }
`;

const LabelPill = styled.span<{ color: string }>`
  background-color: ${props => props.color};
  color: white; padding: 0.1rem 0.4rem; border-radius: 8px; font-size: 0.75rem; margin-right: 0.25rem;
`;

const LoadingSentinel = styled.div`
  height: 20px; display: flex; justify-content: center; align-items: center; color: var(--text-normal); padding: 1rem;
`;

const SortIcon = ({ direction, index }: { direction?: 'asc' | 'desc', index?: number }) => {
    if (!direction) return <span style={{ opacity: 0.1, marginLeft: '4px', fontSize: '0.8em' }}>↕</span>;
    return (
        <span style={{ marginLeft: '4px', color: 'var(--primary-accent)', fontWeight: 'bold', fontSize: '0.9em' }}>
            {direction === 'asc' ? '↑' : '↓'}
            {index !== undefined && <span style={{ fontSize: '0.7em', verticalAlign: 'top', marginLeft: '1px' }}>{index + 1}</span>}
        </span>
    );
};

interface SortCriterion {
    key: string;
    direction: 'asc' | 'desc';
}

type SearchKey = 'title' | 'cardListId' | 'boardId' | 'labels' | 'createDateTime' | 'modifyDateTime' | 'dueDate';

// ✨ 상세 페이지 처리를 위한 별도 컴포넌트
const CardRouteWrapper: React.FC<{ onClose: () => void, onUpdate: (card: Card) => void, onDelete: (id: string) => void }> = ({ onClose, onUpdate, onDelete }) => {
    const { cardId } = useParams<{ cardId: string }>();
    const [card, setCard] = useState<Card | null>(null);
    const user = useAuth();
    const isReadOnly = card?.userId !== user?.id && card?.assigneeId !== user?.id; // 담당자는 진행을 고칠 수 있다

    useEffect(() => {
        const fetchCard = async () => {
            if (cardId) {
                try {
                    const response = await getCardById(cardId);
                    setCard(response.data);
                } catch (error) {
                    console.error("Failed to fetch card", error);
                    onClose();
                }
            }
        };
        fetchCard();
    }, [cardId, onClose]);

    if (!card) return null;

    const handleToggleComplete = async () => {
        const originalCard = { ...card };
        const updated = { ...card, completed: !card.completed };
        setCard(updated); // 로컬 상태 즉시 반영
        try {
            await updateCard({ id: card.id, completed: updated.completed });
            onUpdate(updated); // 부모 리스트 업데이트 (여기서 handleCardUpdate 호출됨)
        } catch(e: any) {
            console.error(e);
            setCard(originalCard);
            // 백엔드에서 보낸 에러 메시지 표시
            const message = e.response?.data?.data || "완료 처리 실패";
            alert(message);
        }
    };

    return (
        <CardDetailModal
            card={card}
            onClose={onClose}
            onUpdate={(updatedCard) => {
                setCard(updatedCard);
                onUpdate(updatedCard); // 부모 리스트 업데이트 (handleCardUpdate)
            }}
            onDelete={() => {
                onDelete(card.id);
                onClose();
            }}
            onToggleComplete={handleToggleComplete}
            fromBoard={true}
            readOnly={isReadOnly}
        />
    );
};

const AllTasksPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [cards, setCards] = useState<Card[]>([]);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [showCompleted, setShowCompleted] = useState(false);

    const [searchType, setSearchType] = useState<SearchKey>('title');
    const [searchValue, setSearchValue] = useState('');
    const [activeSearch, setActiveSearch] = useState<{ type: SearchKey, value: string } | null>(null);

    const [sortCriteria, setSortCriteria] = useState<SortCriterion[]>([
        { key: 'dueDate', direction: 'asc' }
    ]);

    const [boardMap, setBoardMap] = useState<Record<string, string>>({});
    const [listMap, setListMap] = useState<Record<string, string>>({});

    // ✨ [신규] 생성 모달 상태
    const [showCreateModal, setShowCreateModal] = useState(false);

    const [projectMap, setProjectMap] = useState<Record<string, string>>({});

    const availableLabels = useMemo(() => {
        const labelSet = new Set<string>();
        cards.forEach(card => {
            card.labels?.forEach(label => labelSet.add(label.name));
        });
        return Array.from(labelSet).sort();
    }, [cards]);

    useEffect(() => {
        const fetchMetadata = async () => {
            try {
                const boardRes = await getBoardList();
                const boards = boardRes.data._embedded?.getList || [];
                const bMap: Record<string, string> = {};
                boards.forEach(b => bMap[b.id] = b.name);
                setBoardMap(bMap);

                const listPromises = boards.map(b => getListsByBoardId(b.id));
                const listResponses = await Promise.all(listPromises);
                const lMap: Record<string, string> = {};
                listResponses.forEach(res => {
                    const lists = res.data._embedded?.getList || [];
                    lists.forEach(l => lMap[l.id] = l.name);
                });
                setListMap(lMap);

                const projRes = await getProjectList(); // api import 필요
                const projects = projRes.data; // 배열 반환 가정
                const pMap: Record<string, string> = {};
                projects.forEach((p: any) => pMap[p.id] = p.title);
                setProjectMap(pMap);
            } catch (e) {
                console.error("Failed to fetch metadata", e);
            }
        };
        fetchMetadata();
    }, []);

    const observer = useRef<IntersectionObserver | null>(null);
    const lastElementRef = useCallback((node: HTMLDivElement | null) => {
        if (isLoading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prev => prev + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [isLoading, hasMore]);

    const formatSearchDate = (dateString: string): string => {
        if (!dateString) return '';
        let formatted = dateString.replace('T', ' ');
        if (formatted.split(':').length === 2) {
            formatted += ':00';
        }
        if (!formatted.includes('.')) {
            formatted += '.000';
        }
        return formatted;
    };

    const fetchTasks = useCallback(async (pageToFetch: number) => {
        setIsLoading(true);
        try {
            const sortParams = sortCriteria.map(c => `${c.key},${c.direction}`);
            const completedParam = showCompleted ? undefined : false;

            let searchParams: any = { completed: completedParam };

            if (activeSearch && activeSearch.value.trim()) {
                const { type, value } = activeSearch;

                if (type === 'boardId') {
                    const matchedId = Object.keys(boardMap).find(id =>
                        boardMap[id].toLowerCase().includes(value.toLowerCase())
                    );
                    searchParams.boardId = matchedId || 'nomatch';
                } else if (type === 'cardListId') {
                    const matchedId = Object.keys(listMap).find(id =>
                        listMap[id].toLowerCase().includes(value.toLowerCase())
                    );
                    searchParams.cardListId = matchedId || 'nomatch';
                } else if (type === 'labels') {
                    searchParams.labels = [{ name: value, color: '' }];
                } else if (type === 'createDateTime' || type === 'modifyDateTime' || type === 'dueDate') {
                    searchParams[type] = formatSearchDate(value);
                } else {
                    searchParams[type] = value;
                }
            }

            const response = await getAllCardsForUser(pageToFetch, 20, sortParams, searchParams);
            const newCards: Card[] = response.data._embedded?.getList || [];

            setCards(prev => {
                // pageToFetch가 0이면 초기화(덮어쓰기)
                if (pageToFetch === 0) return newCards;
                const existingIds = new Set(prev.map(c => c.id));
                const filteredNewCards = newCards.filter(c => !existingIds.has(c.id));
                return [...prev, ...filteredNewCards];
            });

            if (newCards.length < 20) {
                setHasMore(false);
            } else if (pageToFetch === 0) {
                setHasMore(true);
            }
        } catch (error) {
            console.error("Failed to fetch tasks", error);
        } finally {
            setIsLoading(false);
        }
    }, [sortCriteria, showCompleted, activeSearch, boardMap, listMap]);

    // 조건 변경 시 페이지 0으로 초기화 및 즉시 조회
    useEffect(() => {
        setPage(0);
        fetchTasks(0);
    }, [sortCriteria, showCompleted, activeSearch, fetchTasks]);

    // 페이지 변경(스크롤) 시 추가 조회 (0보다 클 때만)
    useEffect(() => {
        if (page > 0) {
            fetchTasks(page);
        }
    }, [page, fetchTasks]);

    const handleSearch = () => {
        setActiveSearch({ type: searchType, value: searchValue });
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleSort = (key: string) => {
        setSortCriteria(prev => {
            const existingIndex = prev.findIndex(c => c.key === key);
            if (existingIndex >= 0) {
                const currentDir = prev[existingIndex].direction;
                if (currentDir === 'asc') {
                    const newCriteria = [...prev];
                    newCriteria[existingIndex] = { key, direction: 'desc' };
                    return newCriteria;
                } else {
                    return prev.filter((_, idx) => idx !== existingIndex);
                }
            } else {
                return [...prev, { key, direction: 'asc' }];
            }
        });
    };

    const resetSort = () => setSortCriteria([{ key: 'dueDate', direction: 'asc' }]);

    const getSortStatus = (key: string) => {
        const index = sortCriteria.findIndex(c => c.key === key);
        if (index === -1) return { direction: undefined, index: undefined };
        return { direction: sortCriteria[index].direction, index: sortCriteria.length > 1 ? index : undefined };
    };

    // ✨ [수정] 모달에서 호출하는 업데이트 핸들러
    // 변경사항이 있을 때 API를 무조건 다시 호출(fetchTasks(0))하여 목록을 새로고침합니다.
    const handleCardUpdate = (updatedCard: Card) => {
        setPage(0);
        fetchTasks(0);
    };

    const handleCardDelete = async (cardId: string) => {
        await deleteCard(cardId);
        setCards(prev => prev.filter(c => c.id !== cardId));
        navigate('/tasks'); // 상세 닫기
    };

    const formatDate = (dateStr: string | null) => dateStr ? dateStr.split(' ')[0] : '';
    const isDateSearch = searchType === 'createDateTime' || searchType === 'modifyDateTime' || searchType === 'dueDate';

    // ✨ 날짜 포맷팅 (시간만 표시)
    // const formatTimeOnly = (dt?: string) => {
    //     if (!dt) return '';
    //     // "2026-01-08 17:24:53" -> "17:24"
    //     try {
    //         return dt.split(' ')[1].substring(0, 5);
    //     } catch { return dt; }
    // };

// ✨ [수정] 카드 클릭 시 taskCode 우선 사용
    const onCardClick = (card: Card) => { // 인자를 card 객체 전체로 변경
        const targetId = card.taskCode || card.id; // taskCode가 있으면 사용, 없으면 id 사용
        navigate(`/tasks/card/${targetId}${location.search}`);
    };

    const onCloseModal = () => {
        navigate(`/tasks${location.search}`);
    };

    // ✨ 리스트에서 직접 완료 토글 시 핸들러 (낙관적 업데이트 유지)
    // 리스트에서의 단순 클릭은 빠른 반응을 위해 API 재조회 없이 상태만 변경합니다.
    const handleToggleCompleteList = async (e: React.MouseEvent, card: Card) => {
        e.stopPropagation();
        const originalCards = [...cards];
        const updatedCard = { ...card, completed: !card.completed };

        setCards(prev => {
            if (!showCompleted && updatedCard.completed) {
                return prev.filter(c => c.id !== updatedCard.id);
            }
            return prev.map(c => c.id === updatedCard.id ? updatedCard : c);
        });

        try {
            await updateCard({ id: card.id, completed: updatedCard.completed });
        } catch (error: any) {
            console.error("Failed to toggle complete", error);

            setCards(originalCards);

            // ✨ [추가] 에러 메시지 알림
            const message = error.response?.data?.data || "상태 변경 실패";
            alert(message);

            setCards(prev => prev.map(c => c.id === card.id ? card : c));
        }
    };

    const handleProjectClick = (e: React.MouseEvent, projectId: string) => {
        e.stopPropagation(); // 행(Row) 클릭 이벤트(카드 상세 열기) 방지
        if (projectId && projectId !== 'nomatch') {
            navigate(`/projects/${projectId}`);
        }
    };

    // ✨ [신규] 카드 생성 핸들러
    const handleCreateCard = async (boardId: string, listId: string, title: string) => {
        try {
            // 1. 카드 생성 API 호출 (✨ 필수값 boardId, priority 추가)
            const response = await createCard({
                boardId: boardId,
                cardListId: listId,
                title: title,
                priority: 0 // ✨ 0으로 고정
            });
            const newCard = response.data;

            // 2. 모달 닫기
            setShowCreateModal(false);

            // 3. 목록 새로고침 (백그라운드 갱신)
            setPage(0);
            fetchTasks(0);

            // 4. 생성된 카드의 상세 모달로 이동
            navigate(`/tasks/card/${newCard.id}${location.search}`);

        } catch (error) {
            console.error("Failed to create card", error);
            alert("카드 생성에 실패했습니다.");
        }
    };

    return (
        <PageWrapper>
            <ResponsiveHeader
                title="Tasks"
                leftContent={                    <>
                    <Button onClick={() => setShowCreateModal(true)} cyan>+ Create Card</Button>
                    <SearchContainer>
                        <SearchSelect
                            value={searchType}
                            onChange={(e) => {
                                setSearchType(e.target.value as SearchKey);
                                setSearchValue('');
                            }}
                        >
                            <option value="title">제목</option>
                            <option value="cardListId">리스트</option>
                            <option value="boardId">보드</option>
                            <option value="labels">라벨</option>
                            <option value="dueDate">마감일</option>
                            <option value="createDateTime">생성일</option>
                            <option value="modifyDateTime">수정일</option>
                        </SearchSelect>

                        <SearchInput
                            type={isDateSearch ? "datetime-local" : "text"}
                            step={isDateSearch ? "1" : undefined}
                            placeholder={isDateSearch ? "" : "검색어 입력..."}
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            list={searchType === 'labels' ? "label-options" : undefined}
                        />
                        {searchType === 'labels' && (
                            <datalist id="label-options">
                                {availableLabels.map(label => (
                                    <option key={label} value={label} />
                                ))}
                            </datalist>
                        )}

                        <Button onClick={handleSearch} style={{padding: '0.2rem 0.5rem', fontSize: '0.8rem'}}>검색</Button>
                    </SearchContainer>

                    <FilterCheckbox>
                        <input type="checkbox" checked={showCompleted} onChange={(e) => setShowCompleted(e.target.checked)} />
                        완료 업무 표시
                    </FilterCheckbox>

                    {sortCriteria.length > 0 && (
                        <ResetSortButton onClick={resetSort}>정렬 초기화</ResetSortButton>
                    )}
                </>}
            />

            <ContentContainer>
                <ListSection>
                    <TaskTable>
                        <thead>
                        <tr>
                            <th style={{width: '40px'}} onClick={() => handleSort('completed')}>
                                <SortIcon {...getSortStatus('completed')} />
                            </th>
                            {/* ✨ [추가] 상태 컬럼 (모바일에서는 좁을 수 있으므로 상황에 따라 숨기거나 유지) */}
                            <th style={{width: '80px'}} onClick={() => handleSort('state')}>
                                상태 <SortIcon {...getSortStatus('state')} />
                            </th>
                            <th style={{width: '7%'}} onClick={() => handleSort('taskCode')}>
                                업무코드 <SortIcon {...getSortStatus('taskCode')} />
                            </th>
                            <th style={{width: '30%'}} onClick={() => handleSort('title')}>
                                제목 <SortIcon {...getSortStatus('title')} />
                            </th>
                            <th style={{width: '18%'}} onClick={() => handleSort('dueDate')}>
                                기간 <SortIcon {...getSortStatus('dueDate')} />
                            </th>
                            {/* ✨ [신규] 컬럼 추가 (모바일 숨김) */}
                            <th className="mobile-hidden" style={{width: '8%'}} onClick={() => handleSort('inputTime')}>
                                진행 시간 <SortIcon {...getSortStatus('inputTime')} />
                            </th>
                            <th className="mobile-hidden" style={{width: '15%'}} onClick={() => handleSort('labels.name')}>
                                라벨 <SortIcon {...getSortStatus('labels.name')} />
                            </th>
                            <th className="mobile-hidden" style={{width: '10%'}} onClick={() => handleSort('cardListId')}>
                                리스트 <SortIcon {...getSortStatus('cardListId')} />
                            </th>
                            <th className="mobile-hidden" style={{width: '10%'}} onClick={() => handleSort('boardId')}>
                                보드 <SortIcon {...getSortStatus('boardId')} />
                            </th>
                            <th className="mobile-hidden" style={{width: '10%'}} onClick={() => handleSort('projectId')}>
                                프로젝트 <SortIcon {...getSortStatus('projectId')} />
                            </th>
                            <th className="mobile-hidden" style={{width: '10%'}} onClick={() => handleSort('createDateTime')}>
                                생성일 <SortIcon {...getSortStatus('createDateTime')} />
                            </th>
                            <th className="mobile-hidden" style={{width: '10%'}} onClick={() => handleSort('modifyDateTime')}>
                                수정일 <SortIcon {...getSortStatus('modifyDateTime')} />
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        {cards.map((card) => (
                            <TaskRow
                                key={card.id}
                                onClick={() => onCardClick(card)}
                                status={card.status || undefined}
                            >
                                <td style={{textAlign: 'center'}} onClick={(e) => handleToggleCompleteList(e, card)}>
                                    <CheckboxDisplay checked={!!card.completed} />
                                </td>
                                {/* ✨ [추가] 상태 데이터 표시 */}
                                <td style={{textAlign: 'center'}}>
                                    {card.state && (
                                        <ListStatePill bgColor={getStateColor(card.state)}>
                                            {getStateLabel(card.state)}
                                        </ListStatePill>
                                    )}
                                </td>
                                <td>{card.taskCode && <span style={{ color: 'var(--text-normal)', fontSize: '0.85rem', marginRight: '6px' }}>{card.taskCode}</span>}</td>
                                <td title={card.title}>{card.title}</td>
                                <td style={{fontSize: '0.9rem', color: 'inherit'}}>
                                    {formatDate(card.startDate)} ~ {formatDate(card.dueDate)}
                                </td>
                                {/* ✨ [신규] 컬럼 데이터 표시 */}
                                <td className="mobile-hidden" style={{fontSize: '0.9rem'}}>
                                    {formatMsShort(card.inputTime)}
                                </td>
                                <td className="mobile-hidden">{card.labels?.map((label, idx) => (<LabelPill key={idx} color={label.color}>{label.name}</LabelPill>))}</td>
                                <td className="mobile-hidden" style={{fontSize: '0.9rem', color: 'inherit'}} title={card.cardListId}>{listMap[card.cardListId] || '-'}</td>
                                <td className="mobile-hidden" style={{fontSize: '0.9rem', color: 'inherit'}} title={card.boardId}>{boardMap[card.boardId] || '-'}</td>
                                <td
                                    className="mobile-hidden"
                                    style={{
                                        fontSize: '0.9rem',
                                        color: card.projectId ? 'var(--primary-accent)' : 'inherit', // 링크 느낌 나게 색상 변경
                                        cursor: card.projectId ? 'pointer' : 'default',
                                        textDecoration: card.projectId ? 'underline' : 'none'
                                    }}
                                    title={card.projectId}
                                    onClick={(e) => handleProjectClick(e, card.projectId)}
                                >
                                    {projectMap[card.projectId] || '-'}
                                </td>
                                <td className="mobile-hidden" style={{fontSize: '0.9rem'}}>{formatDate(card.createDateTime)}</td>
                                <td className="mobile-hidden" style={{fontSize: '0.9rem'}}>{formatDate(card.modifyDateTime)}</td>
                            </TaskRow>
                        ))}
                        </tbody>
                    </TaskTable>
                    <LoadingSentinel ref={lastElementRef}>
                        {isLoading ? 'Loading...' : hasMore ? 'Scroll for more' : 'End of list'}
                    </LoadingSentinel>
                </ListSection>

                <Routes>
                    <Route
                        path="card/:cardId"
                        element={
                            <CardRouteWrapper
                                onClose={onCloseModal}
                                onUpdate={handleCardUpdate}
                                onDelete={handleCardDelete}
                            />
                        }
                    />
                </Routes>
            </ContentContainer>

            {/* ✨ [신규] 생성 모달 렌더링 */}
            {showCreateModal && (
                <CreateCardSelectModal
                    onClose={() => setShowCreateModal(false)}
                    onCreate={handleCreateCard}
                />
            )}
        </PageWrapper>
    );
};

export default AllTasksPage;